import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import styles from './CustomTraitEditor.module.scss';

import { useTranslate } from 'react-polyglot';
import { useAuth } from '../../../../../../Auth';
import Modal from '../../../../../Shared/Modal/Modal';
import { useAxios } from '../../../../../../AxiosHandler';

/**
 * 
 * CustomTrait in database:
 * ID
 * NAME
 * DATATYPE
 * OPTIONS
 * CREATEDBY
 * COUNCIL_ID
 * BREED_ID
 * CREATEDTIME
 * CUSTOMDATA_JSON
 * 
 */

function CustomTraitEditor(props: CustomTraitEditorProps) {
    const t = useTranslate();
    const auth = useAuth();
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;

    const [customTrait, setCustomTrait] = React.useState<any>();
    const [healthKeys, setHealthKeys] = React.useState<string[]>([]);

    function saveCustomTrait() {
        if (customTrait?.ID == null) {
            axios.single({
                method: 'put',
                url: "/api/dynschema/customtrait/" + auth.selectedBreed,
                data: customTrait,
                headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
            }).then((res: any) => {
                props.onSave?.(res.data);
                setCustomTrait(null);
            }).catch((err: any) => {
                console.log(err);
            });
        } else {
            axios.single({
                method: 'post',
                url: "/api/dynschema/customtrait/" + auth.selectedBreed + "/" + customTrait.ID,
                data: customTrait,
                headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
            }).then((res: any) => {
                props.onSave?.(customTrait.ID);
                setCustomTrait(null);
            }).catch((err: any) => {
                console.log(err);
            });
        }
    }

    function fetchCustomTrait() {
        axios.single({
            method: 'get',
            url: "/api/dynschema/customtrait/" + auth.selectedBreed + "/" + props.editingCustomTrait,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setCustomTrait(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function fetchHealthKeys() {
        axios.single({
            method: 'get',
            url: "/api/dogs/healthstatuskeys/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setHealthKeys(Array.isArray(res.data) ? res.data : []);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    // A dropdown answered «Mistenkt» or «Diagnostisert» in an accepted sickness
    // report is registered on the dog as this health value.
    function getHealthKey(): string {
        try {
            return JSON.parse(customTrait?.CUSTOMDATA_JSON || "{}")?.healthKey ?? "";
        } catch (e) {
            return "";
        }
    }

    function setHealthKey(healthKey: string) {
        let customdata: any = {};
        try {
            customdata = JSON.parse(customTrait?.CUSTOMDATA_JSON || "{}");
        } catch (e) {}
        if (healthKey == "") {
            delete customdata.healthKey;
        } else {
            customdata.healthKey = healthKey;
        }
        setCustomTrait({...customTrait, CUSTOMDATA_JSON: JSON.stringify(customdata)});
    }

    function handleImageUpload(event: React.ChangeEvent<HTMLInputElement>) {
        const files = event.target.files;
        if (files) {
            const fileReaders = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const reader = new FileReader();
                reader.readAsDataURL(file);
                fileReaders.push(
                    new Promise((resolve, reject) => {
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = reject;
                    })
                );
            }
            Promise.all(fileReaders).then((base64Images) => {
                setCustomTrait({
                    ...customTrait,
                    CUSTOMDATA_JSON: JSON.stringify({
                        ...JSON.parse(customTrait.CUSTOMDATA_JSON || "{}"),
                        images: base64Images
                    }),
                });
            });
        }
    }

    function moveImage(index: number, direction: 'up' | 'down') {
        const images = JSON.parse(customTrait.CUSTOMDATA_JSON).images;
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < images.length) {
            const temp = images[newIndex];
            images[newIndex] = images[index];
            images[index] = temp;
            setCustomTrait({
                ...customTrait,
                CUSTOMDATA_JSON: JSON.stringify({
                    ...JSON.parse(customTrait.CUSTOMDATA_JSON),
                    images: images
                }),
            });
        }
    }

    useEffect(() => {
        if (auth.selectedBreed != undefined) fetchHealthKeys();
    }, [auth.selectedBreed]);

    useEffect(() => {
        if (props.editingCustomTrait != null && props.editingCustomTrait != "new") {
            fetchCustomTrait();
        } else if (props.editingCustomTrait === "new") {
            setCustomTrait({
                ID: null,
                NAME: "",
                DATATYPE: "",
                OPTIONS: "",
                CREATEDBY: "system",
                CUSTOMDATA_JSON: JSON.stringify({}),
            });
        }
    }, [props.editingCustomTrait]);

    return (
        <Modal
            title={customTrait?.NAME}
            show={customTrait != null || props.editingCustomTrait === "new"}
            onClose={() => {
                setCustomTrait(null);
                props.onClose?.();
            }}
        >
            <div className={styles.valueeditor}>
                <label>{t("admin.editdynschema.question.name")}</label>
                <input
                    type="text"
                    className="form-control"
                    value={customTrait?.NAME}
                    onChange={(e) => {
                        setCustomTrait({...customTrait, NAME: e.target.value});
                    }}
                />
            </div>
            <div className={styles.valueeditor}>
                <label>{t("admin.editdynschema.question.datatype")}</label>
                <select
                    className="form-control"
                    value={customTrait?.DATATYPE}
                    onChange={(e) => {
                        setCustomTrait({...customTrait, DATATYPE: e.target.value});
                    }}
                >
                    <option value="" disabled>{t("admin.editdynschema.question.selectdatatype")}</option>
                    <option value="string">String</option>
                    <option value="number">Number</option>
                    <option value="dropdown">Dropdown</option>
                    <option value="imagecompare">Sammenligne</option>
                </select>
            </div>
            {customTrait?.DATATYPE === "dropdown" ?
                <div className={styles.valueeditor}>
                    <label>{t("admin.editdynschema.question.dropdownoptions")}</label>
                    <input
                        type="text"
                        className="form-control"
                        value={customTrait?.OPTIONS}
                        onChange={(e) => {
                            setCustomTrait({...customTrait, OPTIONS: e.target.value});
                        }}
                    />
                </div>
            : null}
            {customTrait?.DATATYPE === "dropdown" && (healthKeys.length > 0 || getHealthKey() != "") ?
                <div className={styles.valueeditor}>
                    <label>{t("admin.editdynschema.question.healthkey")}</label>
                    <select
                        className="form-control"
                        value={getHealthKey()}
                        onChange={(e) => {
                            setHealthKey(e.target.value);
                        }}
                    >
                        <option value="">{t("admin.editdynschema.question.healthkeynone")}</option>
                        {[...healthKeys, ...(getHealthKey() != "" && !healthKeys.includes(getHealthKey()) ? [getHealthKey()] : [])].map((key) => (
                            <option key={key} value={key}>{t("dog.details.health.status." + key)}</option>
                        ))}
                    </select>
                    <small className="text-muted">{t("admin.editdynschema.question.healthkeyhelp")}</small>
                </div>
            : null}
            {customTrait?.DATATYPE === "imagecompare" ?
                <div className={styles.valueeditor}>
                    <label>{t("admin.editdynschema.question.imagecompareimages")}</label>
                    {customTrait?.CUSTOMDATA_JSON ?
                        JSON.parse(customTrait.CUSTOMDATA_JSON).images.map((image: string, index: number) => (
                            <div key={index} className={styles.imageContainer}>
                                <img
                                    src={image}
                                    alt=""
                                    style={{maxWidth: "100px", maxHeight: "100px"}}
                                />
                                <button
                                    className="btn btn-success"
                                    onClick={() => moveImage(index, 'up')}
                                    disabled={index === 0}
                                >
                                    ↑
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={() => moveImage(index, 'down')}
                                    disabled={index === JSON.parse(customTrait.CUSTOMDATA_JSON).images.length - 1}
                                >
                                    ↓
                                </button>
                            </div>
                        ))
                    : null}
                    <br/>
                    <input
                        type="file"
                        className="form-control"
                        accept=".svg"
                        multiple
                        onChange={handleImageUpload}
                    />
                </div>
            : null}
            <hr/>
            <div className={styles.actions}>
                <button
                    className="btn btn-success"
                    onClick={() => {
                        saveCustomTrait();
                    }}
                >
                    {t("admin.editdynschema.save")}
                </button>
            </div>
        </Modal>
    );
}

class CustomTraitEditorProps {
    editingCustomTrait?: any;
    onClose?: () => void;
    onSave?: (traitid: number | undefined) => void;
}

export default CustomTraitEditor;